/**
 * UYUM DURUMU — "yazılım bugün neyi yapamıyor ve neden".
 *
 * ⚠ BU DOSYA KURAL TAŞIMAZ. Sunucudaki ibare defteri tek kaynaktır; burada
 * yalnız onun gönderdiği şeklin tipi ve operatörün okuyacağı ADLAR var. Bir
 * muamelenin açık/kapalı olduğunu panelin kendi başına hesaplaması, iki
 * listenin ayrışacağı bir yüzey açardı — ve ayrıştıkları gün panel,
 * çözümleyicinin bloke edeceği bir işlemi "hazır" gösterirdi.
 */

export interface TreatmentReadiness {
  treatment: string;
  availableLanguages: string[];
  missingLanguages: string[];
  blocked: boolean;
}

export interface ConditionalMentionInfo {
  key: string;
  /** Hükmün ibareye bağladığı koşulun METNİ — panelin özeti değil. */
  condition: string;
  evaluable: boolean;
  policy: string;
  rationale: string;
  source: string;
}

export interface VatReadiness {
  primaryLanguage: string;
  blockedInPrimaryLanguage: TreatmentReadiness[];
  treatments: TreatmentReadiness[];
  conditionalMentions: ConditionalMentionInfo[];
}

/**
 * KDV muamelelerinin operatör dilindeki adları.
 *
 * ⚠ SUNUCUNUN KODLARI ÇEVRİLMEZ, AÇIKLANIR. `OUTSIDE_EU` bir koddur ve dallanma
 * için kullanılır; operatörün ekranda göreceği şey "AB dışı hizmet"tir. Kodun
 * kendisi ekrana asla basılmaz — ama karşılığı bulunamazsa basılır, çünkü boş
 * bir satır göstermek bilgiyi tamamen gizlemek olurdu.
 */
export const VAT_TREATMENT_LABELS: Record<string, string> = {
  DOMESTIC: 'Yurtiçi satış',
  DOMESTIC_RC_IMMOVABLE: 'Yurtiçi taşınmaz işi — ters yükümlülük',
  EU_B2B_RC: 'AB B2B hizmet — ters yükümlülük',
  INTRA_EU_SUPPLY_GOODS: 'AB içi mal teslimi (muafiyet)',
  EU_B2C_OSS: 'AB tüketicisine hizmet (OSS)',
  OUTSIDE_EU: 'AB dışı hizmet (Belçika KDV kapsamı dışı)',
  EXPORT: 'AB dışına mal ihracı (muafiyet)',
  EXEMPT_SMALL_BUSINESS: 'Küçük işletme muafiyeti (art. 56bis)',
  EXEMPT_ART44: 'art. 44 muafiyeti',
  CREDIT_NOTE: 'Kredi notu',
};

/** Yasal dilin operatöre gösterilecek adı. */
export const DOCUMENT_LANGUAGE_LABELS: Record<string, string> = {
  fr: 'Fransızca',
  nl: 'Hollandaca',
  en: 'İngilizce',
};

// ─────────────────────────────────────────── uyum takvimi ve eksikler (M8a)

/**
 * ⚠⚠ `dueDate` `null` OLABİLİR ve bu bir eksiklik değil, DÜRÜSTLÜKTÜR: tarihi
 * doğrulanmamış ya da idarece her yıl ilan edilen bir yükümlülük için yazılım
 * tarih UYDURMAZ. Panel bunu "tarih yok — kendiniz doğrulayın" diye gösterir.
 */
export type DueDateBasis = 'DERIVED' | 'ANNOUNCED' | 'UNVERIFIED';
export type ObligationUrgency = 'OVERDUE' | 'DUE_SOON' | 'UPCOMING' | 'NO_DATE';

export const BASIS_LABELS: Record<DueDateBasis, string> = {
  DERIVED: 'Tarih hesaplandı',
  ANNOUNCED: 'Tarihi idare ilan eder',
  UNVERIFIED: 'Tarih DOĞRULANMADI',
};

export interface CalendarEntry {
  kind: string;
  label: string;
  channel: string;
  basis: DueDateBasis;
  note: string;
  periodKey: string;
  dueDate: string | null;
  urgency: ObligationUrgency;
  /** ⚠ Yalnız KANIT varsa `true` — gönderilmiş beyan kaydı gibi. */
  done: boolean;
  evidence: string | null;
  /** Operatörün elle girdiği tarih mi (M8b-2). */
  overridden: boolean;
  /** Elle girilen tarihin yerine geçtiği, yazılımın BUGÜNKÜ hesabı. */
  replacedDueDate: string | null;
  overrideReason: string | null;
  overrideSourceUrl: string | null;
}

export interface OpenItem {
  code: string;
  label: string;
  count: number;
  detail: string | null;
}

// ─────────────────────────────────────────── ilk gün hazırlığı

/**
 * ⚠⚠ BU BİR "HOŞ GELDİNİZ TURU" DEĞİL, BLOKE EDİCİLER LİSTESİDİR. Her kalem
 * somut bir 4xx'e karşılık geliyor: cari yoksa fatura oluşturulamaz
 * (`contactId` zorunlu), kalem yoksa satır eklenemez (`itemId` zorunlu),
 * IBAN yoksa belge düzenlenemez, mali yıl açık değilse deftere kayıt atılamaz.
 *
 * ⚠ METİNLER SUNUCUDAN GELİR. Bir eksiğin NEYİ engellediği ve hukuki gerekçesi
 * kuralın kendisiyle aynı yerde durmalı; panelde kopyalansaydı, kural
 * değiştiğinde ekranda eski gerekçe kalırdı.
 */
export type SetupStepCode = 'IBAN' | 'CONTACT' | 'ITEM' | 'FISCAL_YEAR';

export interface SetupStep {
  code: SetupStepCode;
  done: boolean;
  blocks: string;
  reason: string;
  path: string;
}

export interface SetupReadiness {
  canIssueFirstInvoice: boolean;
  canPostToLedger: boolean;
  steps: SetupStep[];
}

// ─────────────────────────────────────────── hatırlatmalar (M8b-3)

export type ReminderStatus = 'SENT' | 'NOT_SENT' | 'FAILED' | 'SUPERSEDED';
export type ReminderReason =
  | 'DISABLED'
  | 'NO_RECIPIENT'
  | 'INVALID_RECIPIENT'
  | 'NO_API_KEY'
  | 'NO_SENDER'
  | 'PROVIDER_ERROR';

export interface ComplianceReminder {
  id: string;
  obligationKind: string;
  periodKey: string;
  dueDate: string;
  dueDateOverridden: boolean;
  milestone: string;
  scheduledOn: string;
  status: ReminderStatus;
  reason: ReminderReason | null;
  detail: string | null;
  recipient: string | null;
  providerMessageId: string | null;
  collapsedCount: number;
  attempts: number;
  lastAttemptOn: string;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReminderRunSummary {
  today: string;
  candidates: number;
  sent: number;
  notSent: number;
  failed: number;
  superseded: number;
  reason: ReminderReason | null;
}

/**
 * ⚠ Etiket listeleri sunucudaki sabitlerin AYNASIDIR; `compliance-api.spec.ts`
 * ayrışmadıklarını kaynaktan okuyarak kapıda tutuyor. Karşılığı olmayan bir
 * sebep panelde boş bir satır olurdu — operatör hatırlatmanın NEDEN
 * gelmediğini göremezdi.
 */
export const REMINDER_STATUS_LABELS: Record<ReminderStatus, string> = {
  SENT: 'Gönderildi',
  NOT_SENT: 'GÖNDERİLEMEDİ',
  FAILED: 'Hata — yeniden denenecek',
  SUPERSEDED: 'Yerine daha acil hatırlatma geçti',
};

/** Her sebep, operatörün YAPACAĞI şeyi söyler — yalnız ne olduğunu değil. */
export const REMINDER_REASON_LABELS: Record<ReminderReason, string> = {
  DISABLED: 'E-posta bildirimleri kapalı (Ayarlar → Bildirim).',
  NO_RECIPIENT: 'Hatırlatma e-postası adresi girilmemiş (Ayarlar → Bildirim).',
  INVALID_RECIPIENT: 'Hatırlatma e-postası adresi geçerli değil (Ayarlar → Bildirim).',
  NO_API_KEY: 'Resend API anahtarı girilmemiş (Ayarlar → Bildirim).',
  NO_SENDER: 'Sunucuda gönderici adresi (MAIL_FROM) tanımlı değil — sunucu yöneticisine bildirin.',
  PROVIDER_ERROR: 'E-posta sağlayıcısı reddetti ya da ulaşılamadı; sonraki koşumda yeniden denenir.',
};

/** `T30` → "30 gün kala", `T0` → "Son gün", `OVERDUE_3` → "Gecikme uyarısı (3. hafta)". */
export function describeMilestone(milestone: string): string {
  if (milestone === 'T0') return 'Son gün';
  const before = /^T(\d+)$/.exec(milestone);
  if (before) return `${before[1] ?? ''} gün kala`;
  const late = /^OVERDUE_(\d+)$/.exec(milestone);
  if (late) return late[1] === '1' ? 'Gecikme uyarısı' : `Gecikme uyarısı (${late[1] ?? ''}. hafta)`;
  return milestone;
}
